import videojs from "video.js";
import {detectHLS} from "@utilities/DetectHLS";

const Plugin = videojs.getPlugin("plugin");

// Default options for the plugin.
const defaults = {};

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class ExternalPTSDiscontinuity extends Plugin
{

	/**
   * Create a ExternalPTSDiscontinuity plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
	constructor(player, options)
	{
		// The parent class will add player under this.player
		super(player);
		this.options = videojs.mergeOptions(defaults, options);

		this.hlsjs = null;
		if (videojs.Html5Hlsjs)
		{
			videojs.Html5Hlsjs.addHook("beforeinitialize", (x, hlsjs) =>
			{
				this.hlsjs = hlsjs;
			});
		}

		player.ready(() =>
		{
			if (player.detectErrors)
			{
				player.detectErrors().register(this);
			}
		});
	}

	test()
	{
		return new Promise(resolve =>
		{
			// We only support detecting bad HLS videos
			if (!detectHLS(this.player))
			{
				resolve([]);
				return;
			}

			// For the time being, we only support error detection with HLS.js
			// TODO: Add support for VideoJS HTTP Streaming
			if (!this.hlsjs)
			{
				resolve([]);
				return;
			}

			const errors = [];

			// We want to check out the M3U8 for the most recently played level
			const level = this.hlsjs.abrController.lastLoadedFragLevel || this.hlsjs.currentLevel;

			if (level < 0 || level >= this.hlsjs.levels.length)
			{
				resolve([]);
				return;
			}

			const details = this.hlsjs.levels[level].details;
			const fragments = details.fragments;
			let lastPTS = -1;
			let lastFrag = null;
			for (var i = 0; i < fragments.length; i++)
			{
				const fragment = fragments[i];
				const start = fragment.maxStartPTS;
				if (lastPTS !== -1 && start > lastPTS && Math.abs(lastPTS - start) > 1)
				{
					// When a discontinuity exists between two TS files, seeking
					// into the second TS file should work.
					// We'll ignore the boundary +/- 1 second
					errors.push({
						msg: `Discontinuity between two TS files (${lastPTS.toFixed(3)} vs ${start.toFixed(3)}) at: ` +
							 fragment._url,
						recoverable: true,
						corruptRange: videojs.createTimeRanges(
							lastFrag.start + lastFrag.duration - 1,
							fragment.start + 1
						),
						corruptRendition: null
					});
				}
				lastPTS = fragment.endPTS;
				lastFrag = fragment;
			}
			resolve(errors);
		});
	}
}

// Register the plugin with video.js.
videojs.registerPlugin("errorDetectionExternalPTSDiscontinuity", ExternalPTSDiscontinuity);

export default ExternalPTSDiscontinuity;
